import { parseSSEData, createSSEDecoder, processSSEChunk } from './sseParser';
import { ROUTES } from "./constants";
import { readToken } from '../auth/session';

/**
 * Expands a flat object whose keys may use dot notation into a nested object.
 * e.g. { "user.name": "x", "user.age": "1" } -> { user: { name: "x", age: "1" } }
 * @param {Record<string, unknown>} flat
 * @returns {Record<string, unknown>}
 */
function dotObjectParse(flat) {
  const result = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split(".");
    let cursor = result;
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (typeof cursor[part] !== "object" || cursor[part] === null) {
        cursor[part] = {};
      }
      cursor = cursor[part];
    }
    cursor[parts[parts.length - 1]] = value;
  }
  return result;
}

async function request(
  url,
  method,
  body = null,
  contentType = "application/json",
  headers = {}
) {
  const token = readToken();
  headers = {
    ...headers,
    Authorization: token ? `Bearer ${token}` : "",
    "Content-Type": contentType + ";charset=utf-8",
    // "Content-Type": contentType,
  };

  return window
    .fetch(url, {
      method,
      body: body != null ? JSON.stringify(body) : null,
      headers,
      credentials: "include",
    })
    .then((res) => {
      // Check if the response status is Unauthorized (401) - but not from landing page
      if (res.status === 401 && window.location.pathname !== ROUTES.SIGN_IN && window.location.pathname !== '/') {
        window.location.href = ROUTES.SIGN_IN;
        return Promise.reject('Unauthorized');
      }

      const contentType = res.headers.get("content-type") || '';
      let data = null;
      if (contentType.includes("application/json")) {
        data = res.json();
      } else if (contentType.includes("octet-stream")) {
        data = res.blob();
      } else {
        data = res.text();
      }
      return data; // Return only the data
    })
    .catch((err) => {
      console.error(err);
      throw err;
    });
}

  
  async function requestFormData(url, method, body = null, headers = {}) {
    const token = readToken();
  
    // Add Authorization header if token exists
    headers = {
      ...headers,
      Authorization: token ? `Bearer ${token}` : "",
    };
  
    return window
      .fetch(url, {
        method,
        body: body,
        headers,
        credentials: "include",
      })
      .then((res) => {
         if (res.status === 401 && window.location.pathname !== ROUTES.SIGN_IN && window.location.pathname !== '/') {
          window.location.href = ROUTES.SIGN_IN;
          return;
        }
  
        const contentType = res.headers.get("content-type") || '';
        let data = null;
        if (contentType.includes("application/json")) {
          data = res.json();
        } else if (contentType.includes("octet-stream")) {
          data = res.blob();
        } else {
          data = res.text();
        }
        return Promise.all([res, data]);
      })
      .then(([res, data]) => ({
        response: res,
        data,
      }))
      .catch((err) => {
        console.error(err);
        throw err;
      });
  }
  
  function handleSubmit(e, callback) {
    const method = e.target.getAttribute("method") || e.target.method;
    const encType = e.target.getAttribute("encType") || e.target.enctype;
    let body = {};
    for (let field of new FormData(e.target)) {
      body[field[0]] = field[1];
    }
    body = dotObjectParse(body);
    request(e.target.action, method, body, encType)
      .then((res) => {
        callback(res, null);
      })
      .catch((err) => {
        callback(null, err);
      });
  }

  const createSSERequest = (url, formData, onProgress, onComplete, onError) => {
    const token = readToken();
    const abortController = new AbortController(); 
    let progressReceived = false;
    
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        if (!progressReceived) {
          console.error('❌ SSE Request timeout - no data received in 30 seconds');
          onError && onError(new Error('Upload timeout - no progress received. Server may be unreachable.'));
          abortController.abort();
          reject(new Error('SSE request timeout'));
        }
      }, 30000); // 30 second timeout
      
      fetch(url, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include',
        signal: abortController.signal,
      })
      .then(response => {
        if (response.status === 401 && window.location.pathname !== '/') {
          window.location.href = ROUTES.SIGN_IN;
          reject(new Error('Unauthorized'));
          return;
        }
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const reader = response.body.getReader();
        const decoder = createSSEDecoder();
        let buffer = '';
        
        function readStream() {
          reader.read().then(({done, value}) => {
            if (done) {
              clearTimeout(timeout);
              if (buffer.trim()) {
                // Process any remaining data in buffer
                const events = processSSEChunk(buffer);
                events.forEach(event => {
                  const data = parseSSEData(event);
                  if (data && data.type === 'progress') {
                    onProgress && onProgress(data);
                  }
                });
              }
              resolve();
              return;
            }
            
            progressReceived = true; // Mark that we've received at least some data
            const chunk = decoder.decode(value, {stream: true});
            buffer += chunk;
            
            
            // Try to process complete events
            const lines = buffer.split('\n');
            
            // Keep the last incomplete line in the buffer
            buffer = lines[lines.length - 1];
            
            // Process all complete lines
            for (let i = 0; i < lines.length - 1; i++) {
              const line = lines[i].trim();
              if (line) {
                const data = parseSSEData(line);
                if (data) {
                  if (data.type === 'progress') {
                    onProgress && onProgress(data);
                  } else if (data.type === 'error') {
                    console.error('❌ Server error:', data.errorMessage);
                    onError && onError(new Error(data.errorMessage));
                  } else {
                    // Final response
                    clearTimeout(timeout);
                    resolve(onComplete ? onComplete(data) : data);
                    return;
                  }
                }
              }
            }
            
            readStream();
          }).catch(error => {
            clearTimeout(timeout);
            console.error('❌ Stream read error:', error);
            onError && onError(error);
            reject(error);
          });
        }
        
        readStream();
      })
      .catch(error => {
        clearTimeout(timeout);
        console.error('❌ Fetch error:', error);
        onError && onError(error);
        reject(error);
      });
    });
  };
  
  export const cancelUpload = async (projectId) => {
    const token = readToken();
    try {
      const response = await fetch(`/api/collection/cancel-upload/${projectId}`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to cancel upload');
      }
    } catch (error) {
      console.error('Error cancelling upload:', error);
      throw error;
    }
  };
  
  
  export {
    request,
    handleSubmit,
    requestFormData,
    createSSERequest
  };
  